import { Clock } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { useI18nStore } from "../../stores/i18nStore";
import type { ReadingAge, Tone, VitalStatus } from "./homeLogic";
import { ageText, statusText } from "./vitalText";

export interface VitalTile {
  key: string;
  label: string;
  value: string;
  unit: string;
  status: VitalStatus | null;
  age: ReadingAge;
  stale: boolean;
}

interface Props {
  tiles: VitalTile[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
}

const PILL: Record<Tone, string> = {
  ready: "bg-health-100 text-health-700",
  attention: "bg-travel-100 text-travel-700",
  neutral: "bg-primary-50 text-primary-700",
};

/**
 * Three or four readings with their status in words and their age in words
 * (UX_CONCEPT.md §5.1). The old ">4W" badge meant "older than four weeks" and
 * never showed for a reading without a date.
 */
export const VitalsGlance: React.FC<Props> = ({
  tiles,
  loading,
  failed,
  onRetry,
}) => {
  const { t } = useI18nStore();

  return (
    <section aria-labelledby="home-glance">
      <div className="flex items-center justify-between">
        <h2
          id="home-glance"
          className="font-display text-[19px] font-bold text-ink"
        >
          {t.homeAtAGlance}
        </h2>
        <Link
          to="/traveler/health"
          className="flex min-h-[44px] items-center text-sm font-semibold text-primary-700"
        >
          {t.homeAllHealthData}
        </Link>
      </div>

      {loading ? (
        <div className="mt-1 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-[124px] rounded-[18px] bg-white animate-pulse"
            />
          ))}
        </div>
      ) : failed ? (
        <div className="mt-1 flex items-center justify-between gap-3 rounded-[18px] bg-white p-4 text-sm text-muted-foreground">
          <span>{t.homeVitalsError}</span>
          <button
            type="button"
            onClick={onRetry}
            className="min-h-[44px] rounded-xl border border-input px-4 font-semibold text-ink"
          >
            {t.homeRetry}
          </button>
        </div>
      ) : tiles.length === 0 ? (
        <div className="mt-1 flex items-center justify-between gap-3 rounded-[18px] bg-white p-4 text-sm text-muted-foreground">
          <span>{t.homeNoVitals}</span>
          <Link
            to="/traveler/health"
            className="flex min-h-[44px] items-center whitespace-nowrap rounded-xl bg-ink px-4 font-semibold text-white"
          >
            {t.stepBpAction}
          </Link>
        </div>
      ) : (
        <ul className="mt-1 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
          {tiles.map((tile) => (
            <li
              key={tile.key}
              className="flex flex-col gap-1.5 rounded-[18px] bg-white px-3.5 py-3.5"
            >
              <div className="text-xs font-semibold text-muted-foreground">
                {tile.label}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="font-display text-[22px] font-bold text-ink">
                  {tile.value}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {tile.unit}
                </span>
              </div>
              {tile.status && (
                <span
                  className={`self-start rounded-full px-2 py-0.5 text-[11px] font-bold ${PILL[tile.status.tone]}`}
                >
                  {statusText(tile.status, t)}
                </span>
              )}
              <div
                className={`flex items-center gap-1 text-[11px] ${
                  tile.stale
                    ? "font-semibold text-travel-700"
                    : "text-muted-foreground"
                }`}
              >
                <Clock className="h-3 w-3" aria-hidden="true" />
                {ageText(tile.age, t)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
